import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { SxProps, Theme } from '@mui/material/styles';

/** Generic accessible names — not configurable by callers. */
export const BULK_ACTION_BAR_ARIA_LABELS = {
    toolbar: 'Bulk actions toolbar',
    dismiss: 'Close bulk actions',
} as const;

const bulkActionBarRootSx = (theme: Theme): SxProps<Theme> => ({
    position: 'fixed',
    left: '50%',
    bottom: theme.spacing(3),
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.snackbar,
    bgcolor: theme.palette.other.actionBarBackground,
    borderRadius: 2,
    px: 2,
    py: 1,
    boxShadow: 6,
    maxWidth: 'min(960px, calc(100vw - 32px))',
    width: 'max-content',
});

const bulkActionBarSelectionSummaryBoxSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
};

const bulkActionBarSelectionTypographySx: SxProps<Theme> = {
    color: 'common.white',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 0.5,
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
};

const bulkActionBarSelectionCountSx: SxProps<Theme> = {
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
};

const bulkActionBarSelectionLabelSx: SxProps<Theme> = {
    lineHeight: 1,
};

const bulkActionBarPrimaryButtonSx: SxProps<Theme> = {
    typography: 'body2',
    bgcolor: 'secondary.main',
    color: 'secondary.contrastText',
    textTransform: 'none',
    fontWeight: 500,
    flexShrink: 0,
    '&:hover': { bgcolor: 'secondary.containedHoverBackground' },
};

const bulkActionBarCloseIconButtonSx = (useTwoRowLayout: boolean): SxProps<Theme> => ({
    color: 'common.white',
    flexShrink: 0,
    alignSelf: useTwoRowLayout ? 'flex-start' : 'center',
});

const bulkActionBarCompactLayoutRowSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 1,
};

const bulkActionBarCompactColumnStackSx: SxProps<Theme> = {
    minWidth: 'max-content',
};

const bulkActionBarWideLayoutRowSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 1.5,
};

const bulkActionBarFormControlLabelSx = (theme: Theme): SxProps<Theme> => ({
    m: 0,
    ml: 0,
    mr: 0,
    alignItems: 'center',
    gap: 0.75,
    flexWrap: 'nowrap',
    width: 'max-content',
    '& .MuiFormControlLabel-label': {
        ...theme.typography.body2,
        color: theme.palette.common.white,
        fontWeight: 500,
        pl: 0,
        whiteSpace: 'nowrap',
    },
});

const bulkActionBarFormControlLabelWideSx = (theme: Theme): SxProps<Theme> => ({
    ...bulkActionBarFormControlLabelSx(theme),
    flex: '0 1 auto',
});

export type BulkActionBarFilterToggle = {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
};

export type Props = {
    selectedCount: number;
    actionButtonText: string;
    onBulkActionClick: () => void;
    actionButtonTestId?: string;
    filterToggle?: BulkActionBarFilterToggle;
    onDismiss: () => void;
};

const BulkActionBar = ({
    selectedCount,
    actionButtonText,
    onBulkActionClick,
    actionButtonTestId = 'bulk-action-bar-primary-action',
    filterToggle,
    onDismiss,
}: Props) => {
    const theme = useTheme();
    const isCompactLayout = useMediaQuery(theme.breakpoints.down('md'));
    const useTwoRowLayout = Boolean(filterToggle) && isCompactLayout;

    const filterSwitch = filterToggle ? (
        <Switch
            checked={filterToggle.checked}
            onChange={(_, checked) => filterToggle.onChange(checked)}
            color="secondary"
            data-testid="bulk-action-bar-filter-toggle"
        />
    ) : null;

    const countAndPrimaryRow = (
        <Stack direction="row" useFlexGap spacing={1.5} flexWrap="nowrap" alignItems="center">
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="bulk-action-bar-selection-summary"
                sx={bulkActionBarSelectionSummaryBoxSx}
            >
                <Typography component="span" variant="body2" sx={bulkActionBarSelectionTypographySx}>
                    <Box component="span" sx={bulkActionBarSelectionCountSx}>
                        {selectedCount}
                    </Box>{' '}
                    <Box component="span" sx={bulkActionBarSelectionLabelSx}>
                        Selected
                    </Box>
                </Typography>
            </Box>
            <Button
                type="button"
                size="small"
                variant="contained"
                onClick={onBulkActionClick}
                data-testid={actionButtonTestId}
                sx={bulkActionBarPrimaryButtonSx}
            >
                {actionButtonText}
            </Button>
        </Stack>
    );

    const closeButton = (
        <IconButton
            type="button"
            onClick={onDismiss}
            aria-label={BULK_ACTION_BAR_ARIA_LABELS.dismiss}
            data-testid="bulk-action-bar-dismiss"
            sx={bulkActionBarCloseIconButtonSx(useTwoRowLayout)}
            size="small"
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    );

    const filterLabelRow =
        filterToggle && filterSwitch ? (
            <FormControlLabel
                sx={bulkActionBarFormControlLabelSx(theme)}
                control={filterSwitch}
                label={filterToggle.label}
            />
        ) : null;

    return (
        <Box
            data-testid="bulk-action-bar"
            role="toolbar"
            aria-label={BULK_ACTION_BAR_ARIA_LABELS.toolbar}
            sx={bulkActionBarRootSx(theme)}
        >
            {useTwoRowLayout ? (
                <Box sx={bulkActionBarCompactLayoutRowSx}>
                    <Stack direction="column" spacing={0.75} useFlexGap sx={bulkActionBarCompactColumnStackSx}>
                        {countAndPrimaryRow}
                        {filterLabelRow}
                    </Stack>
                    {closeButton}
                </Box>
            ) : (
                <Box sx={bulkActionBarWideLayoutRowSx}>
                    {filterToggle ? (
                        <>
                            {countAndPrimaryRow}
                            <FormControlLabel
                                sx={bulkActionBarFormControlLabelWideSx(theme)}
                                control={filterSwitch}
                                label={filterToggle.label}
                            />
                            {closeButton}
                        </>
                    ) : (
                        <>
                            {countAndPrimaryRow}
                            {closeButton}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default BulkActionBar;
